import { Alert, Button, Input, Modal, Spin, Tree, Typography } from "@arco-design/web-react";
import { useMemo, useState } from "react";

import { MAX_STEP_MEMBERS } from "../config";
import { filterMemberTree, formatMemberName } from "../service/approvalSettingsService";
import type { ApprovalMemberTreeNode } from "../types";

type ApprovalMemberPickerProps = {
  /** 弹窗标题，为空时关闭。 */
  title: string | null;
  tree: ApprovalMemberTreeNode[];
  selectedIds: number[];
  loading: boolean;
  errorMessage: string;
  onRetry: () => void;
  onChange: (memberIds: number[]) => void;
  onClose: () => void;
};

/** 某一级的挑人弹窗：部门—员工树可搜索，只有员工可勾，停用员工不能新勾。 */
export function ApprovalMemberPicker({ title, tree, selectedIds, loading, errorMessage, onRetry, onChange, onClose }: ApprovalMemberPickerProps) {
  const [keyword, setKeyword] = useState("");
  const filtered = useMemo(() => filterMemberTree(tree, keyword), [keyword, tree]);
  return (
    <Modal
      title={title ?? ""}
      visible={title !== null}
      onCancel={onClose}
      afterClose={() => setKeyword("")}
      footer={<Button type="primary" onClick={onClose}>完成</Button>}
      unmountOnExit
    >
      <div className="approval-member-picker">
        <Input.Search allowClear aria-label="搜索部门或员工" placeholder="搜索部门或员工" value={keyword} onChange={setKeyword} />
        <Typography.Text type="secondary">已选 {selectedIds.length} 人（最多 {MAX_STEP_MEMBERS} 人），其中一人同意即进入下一级。</Typography.Text>
        {errorMessage && <Alert type="error" content={errorMessage} action={<Button size="mini" onClick={onRetry}>重试</Button>} />}
        <Spin loading={loading} block>
          <div className="approval-member-tree">
            <Tree
              key={keyword}
              blockNode
              checkable
              checkStrictly
              treeData={toTreeData(filtered)}
              checkedKeys={selectedIds.map((userId) => `user:${userId}`)}
              defaultExpandedKeys={collectKeys(filtered)}
              onCheck={(_, extra) => {
                const userId = Number(String((extra.node as unknown as { key: string }).key).replace("user:", ""));
                onChange(extra.checked ? [...selectedIds, userId] : selectedIds.filter((id) => id !== userId));
              }}
            />
          </div>
        </Spin>
      </div>
    </Modal>
  );
}

/** 映射成 Arco Tree 数据：部门不可勾，停用员工带标注且复选框禁用。 */
function toTreeData(nodes: ApprovalMemberTreeNode[]): Array<Record<string, unknown>> {
  return nodes.map((node) => ({
    key: node.key,
    title: formatMemberName(node.title, node.disabled),
    checkable: node.userId !== undefined,
    disableCheckbox: node.disabled,
    children: toTreeData(node.children),
  }));
}

/** 全部节点 key，搜索后默认全展开。 */
function collectKeys(nodes: ApprovalMemberTreeNode[]): string[] {
  return nodes.flatMap((node) => [node.key, ...collectKeys(node.children)]);
}
